// Source + store → the lines the grid shows. Pure: the same function serves the Worklist and
// History, the browser and the server's reports.
//
// - A Deposco SO is one of a line's pick tickets (`deposco.ts`): its line is one customer + PO (+ DC),
//   its key `DEP|…` beside A2000's `WMS|…`; everything below works for both.
// - One line per WMS # (V7 step 2, Isaac): A2000 groups, the app never regroups. Every open pick
//   ticket with a WMS # is on the line of its WMS, whatever its customer, division, warehouse or
//   dates (several of them: Mixed, information only). One with no WMS # yet is a "No WMS" row of
//   its own, read-only, until A2000 gives it one.
// - A stored line keeps the pick tickets that have a copy on it: a `line-pick-ticket` record or its
//   own baseline (`baseline.ts`); the record wins. Saved warehouse values always win, source fields
//   are never stored.
// - A pick ticket whose copy is on another line came from there: that line's entries come with it
//   (`carry.ts`), and the line it left is UPDATED (a pick ticket removed).
// - Review: New, Approved, Updated (A2000 moved FIRST SHIP, CANCEL or QTY, or a pick ticket came or
//   went), Split (its pick tickets came from other WMS lines) and No WMS.
// - A line from before WMS grouping (the move-over) is read as finished, "Regrouped by WMS": its
//   pick tickets are on their WMS lines now, with its entries.

import type { A2000Shipped } from "./a2000";
import { a2000Shipped } from "./a2000";
import { NO_BASELINE } from "./baseline";
import type { CarryConflicts, CarrySource } from "./carry";
import {
  carriedFromText,
  carriedIds,
  carryOver,
  decodeConflicts,
  encodeConflicts,
  mergeConflicts,
  sourceAbout,
} from "./carry";
import { asDay } from "./days";
import type { DeposcoInfo, DeposcoRow, DeposcoShipped, DeposcoStatus, LineSource } from "./deposco";
import {
  DEPOSCO_PREFIX,
  deposcoInfo,
  deposcoKey,
  deposcoShipped,
  deposcoStatus,
  isDeposcoKey,
  isSoNo,
} from "./deposco";
import type { DivisionRef } from "./divisions";
import type { StatusCategory } from "./routing-status";
import { statusCategory } from "./routing-status";
import { divisionNamer } from "./divisions";
import type { CustomerFace } from "./customer-names";
import { customerNamer, withCustomerNames } from "./customer-names";
import type { ExcelInfo } from "./excel";
import type { OutsideChange } from "./integrity";
import type { RoutingRule, RuleBase, RuleContext, RuleSource } from "./routing-rules";
import { NO_RULE, routingRuleFor, ruleBase, ruleContext } from "./routing-rules";
import { copyProblem, fieldChanges, readBaseline } from "./integrity";
import type { PeField, PeStored } from "./pe-order";
import { isPeField, peFields, peStatusTimes, storedLineFields } from "./pe-order";
import { ptText, ptWord } from "./pt-text";
import type {
  CustomerNameRecord,
  DivisionAbbrevRecord,
  RoutingRuleRecord,
  FinishedStatus,
  LinePickTicketRecord,
  RoutingLineRecord,
  SourceRow,
  WarehouseField,
  ListValueRecord,
  PickupRecord,
  PtShipmentRow,
  ShipmentRecord,
  ShippingRowRecord,
  StoredShipping,
  WarehouseValues,
} from "./types";
import { WAREHOUSE_FIELDS } from "./types";

/** A WMS line's grouping key: its WMS #. */
export const WMS_PREFIX = "WMS|";
export const wmsKey = (wms: string) => `${WMS_PREFIX}${wms}`;
/**
 * A line grouped as today: one WMS #, or one Deposco customer + PO (+ DC) — not one from before WMS
 * grouping (the move-over's sources).
 */
export const isGroupedLine = (l: Pick<RoutingLineRecord, "groupKey">) =>
  l.groupKey.startsWith(WMS_PREFIX) || isDeposcoKey(l.groupKey);
const wmsOf = (l: Pick<RoutingLineRecord, "groupKey" | "wmsNo">) =>
  l.wmsNo || (l.groupKey.startsWith(WMS_PREFIX) ? l.groupKey.slice(WMS_PREFIX.length) : "");
/** A stored line's group: `WMS|<wms>` or its Deposco key; "" from before WMS grouping. */
const groupOf = (l: Pick<RoutingLineRecord, "groupKey" | "wmsNo">) =>
  isDeposcoKey(l.groupKey) ? l.groupKey : wmsOf(l) ? wmsKey(wmsOf(l)) : "";
/** An open pick ticket's group: its WMS #, or its Deposco line; "" for one with no WMS # yet. */
const groupOfSource = (s: SourceRow) =>
  s.deposco ? deposcoKey(s) : s.in_wms === 1 && s.wms_shipment ? wmsKey(String(s.wms_shipment)) : "";

/** Where a line comes from, by its key: `DEP|…` is Deposco's. */
export const sourceOfKey = (groupKey: string): LineSource => (isDeposcoKey(groupKey) ? "Deposco" : "A2000");

/**
 * How the team names a line: "WMS 47001", or a Deposco line's "PO 50264439 · DC 699"; "" for one
 * with neither (No WMS, a workbook row).
 */
export const lineRef = (l: Pick<LineView, "source" | "wmsNo" | "pos" | "dcs">) =>
  l.source === "Deposco"
    ? [l.pos[0] ? `PO ${l.pos[0]}` : "", l.dcs[0] ? `DC ${l.dcs[0]}` : ""].filter(Boolean).join(" · ")
    : l.wmsNo
      ? `WMS ${l.wmsNo}`
      : "";

/** A Deposco key's own words, for a line known by its key alone (a carry-over source). */
const deposcoPlace = (groupKey: string) => {
  const [, po, dc] = groupKey.slice(DEPOSCO_PREFIX.length).split("|");
  return ["Deposco", po ? `PO ${po}` : "", dc ? `DC ${dc}` : ""].filter(Boolean).join(" ");
};

export const VIRTUAL_PREFIX = "auto:";
export const isVirtualKey = (key: string) => key.startsWith(VIRTUAL_PREFIX);
/** A pick ticket with no WMS # yet: its own row. */
export const NO_WMS_PREFIX = "nowms:";

/** One pick ticket of a line: the source now, the copy as last approved, or both. */
export interface PtView {
  pickTicket: string;
  source: SourceRow | null;
  /** Its copy on this line; or, for one that came from another line, its copy there. */
  copy: LinePickTicketRecord | null;
  /** The line it came from, when its copy is on another line. */
  cameFrom: string | null;
  /** On a stored line, but no longer open in A2000. */
  gone: boolean;
  orderNo: string;
  customerPo: string;
  customerCode: string;
  customerName: string;
  corp: string;
  div: string;
  division: string;
  store: string;
  ptDate: string | null;
  start: string | null;
  cancel: string | null;
  qty: number;
  value: number | null;
  wms: string;
  wh: string;
  /** Its customer's routing rule in use (`routingRuleFor`, set by `assemble`), and where it came from. */
  openBdays: number | null;
  dueBdays: number | null;
  rule?: RuleSource;
  /** Its own CUST_REF1 / CUST_REF2 as A2000 gave them (the row's, a copy's); none on Deposco. */
  a2000Rule: RoutingRule;
  /** The DC it ships to: its name, else its number; "" for none (and for a copy, which keeps none). */
  dc: string;
  /** A Deposco SO's own columns: the open SO's, or a gone one's as Deposco closed it; null on A2000. */
  deposco: DeposcoInfo | null;
}

export type Review = "new" | "approved" | "updated" | "split" | "nowms";
/** How the Review column says it (Isaac: the names can change). */
export const REVIEW_LABELS: Record<Review, string> = {
  new: "New",
  approved: "Approved",
  updated: "Updated",
  split: "Split",
  nowms: "No WMS",
};
/**
 * What changed since approval, each shown on its own column's cell (`pts`: SO / PT #): the source's
 * (A2000 or Deposco) and the team's dates (`TEAM_REVIEW_FIELDS`).
 */
export type ChangedField = "start" | "cancel" | "ptDate" | "qty" | "po" | "pts" | TeamReviewField;
export type Changes = Partial<Record<ChangedField, { from: string | number | null; to: string | number | null }>>;

/**
 * The fields people type that make an Approved line Updated when they differ from their value as
 * approved (the field beside each, written with the baseline), whoever changed them. Appointment
 * date (the pickup's) and Routed on (the app's) are not among them.
 */
export const TEAM_REVIEW_FIELDS = { readyDate: "approvedReadyDate", schedShip: "approvedSchedShip" } as const;
export type TeamReviewField = keyof typeof TEAM_REVIEW_FIELDS;
export const isTeamReviewField = (f: string): f is TeamReviewField => f in TEAM_REVIEW_FIELDS;

/** A source date of one pick ticket (SO) changed since approval: any of them, not only the line's earliest. */
export interface PtChange {
  pickTicket: string;
  field: "start" | "cancel" | "ptDate";
  from: string | null;
  to: string | null;
}

/** What differs inside one WMS: how many customers, divisions, warehouses, first ship and cancel dates. */
export interface Mixed {
  customers: number;
  divisions: number;
  warehouses: number;
  starts: number;
  cancels: number;
}

/** "2 customers · 3 cancel dates": the Mixed chip's tooltip. */
export function mixedText(m: Mixed): string {
  const parts: [number, string, string][] = [
    [m.customers, "customer", "customers"],
    [m.divisions, "division", "divisions"],
    [m.warehouses, "warehouse", "warehouses"],
    [m.starts, "first ship date", "first ship dates"],
    [m.cancels, "cancel date", "cancel dates"],
  ];
  return parts
    .filter(([n]) => n > 1)
    .map(([n, , many]) => `${n} ${many}`)
    .join(" · ");
}

/** A customer of a line: A2000 CUSTOMER and CUST_NAME (a Deposco trading partner's). A workbook row has the name only. */
export interface CustomerRef {
  code: string;
  name: string;
  /** Which source's rule shows it (`customer-names.ts`). */
  source: LineSource;
  /** How the pages show it: its shown name, else the source's rule (`withCustomerNames`). */
  face?: CustomerFace;
}

/** What a Deposco line adds: its SOs' status (MIXED), the flag and the Deposco numbers. */
export interface DeposcoLine {
  status: DeposcoStatus | null;
  /** Its SOs Deposco shipped: a flag only, as Shipped in A2000. */
  shipped: DeposcoShipped | null;
  dlixes: string[];
  cos: string[];
  customerOrders: string[];
  groups: string[];
}

export interface LineView {
  /** The stored id, `auto:WMS|<wms>` (`auto:DEP|…`) for a line nobody has touched, `nowms:<pt>` for a No WMS row. */
  key: string;
  stored: RoutingLineRecord | null;
  groupKey: string;
  /** A2000 or Deposco (`deposco.ts`); a workbook row is A2000. */
  source: LineSource;
  /** A Deposco line's own; null on A2000. */
  deposco: DeposcoLine | null;
  /** The WMS #; "" on a No WMS row and a line from before WMS grouping. */
  wmsNo: string;
  pts: PtView[];
  /** Copies on this line whose pick tickets are open on another line now (or have no WMS #). */
  removed: PtView[];
  /** The first value of each (the earliest first ship's pick ticket), and every value. */
  corp: string;
  customerCode: string;
  customerName: string;
  customerNames: string[];
  /** Each customer once, A2000 CUSTOMER (the code the grid shows) and CUST_NAME, the first one first. */
  customers: CustomerRef[];
  div: string;
  division: string;
  divisions: string[];
  /** Each division once, with the abbreviation the pages show (`divisions.ts`), the first one first. */
  divs: DivisionRef[];
  wh: string;
  whs: string[];
  /** Latest PT date; earliest first ship and earliest cancel, and every one; QTY of the open pick tickets. */
  ptDate: string | null;
  /** Every PT date (RECEIVED DATE), earliest first: the date filter keeps the line if any is in range. */
  ptDates: string[];
  start: string | null;
  starts: string[];
  cancel: string | null;
  cancels: string[];
  qty: number;
  /** The $ value — null unless the person is a manager. */
  value: number | null;
  wms: string;
  /** The DCs of its open pick tickets (DC grouping waits for DEPOSCO). */
  dcs: string[];
  orderNos: string[];
  pos: string[];
  /** Several customers, divisions, warehouses or dates in one WMS; null when all agree. */
  mixed: Mixed | null;
  review: Review;
  changes: Changes;
  /** Each pick ticket's (SO's) own date changes since approval (`sinceApproval`). */
  ptChanges: PtChange[];
  fields: WarehouseValues;
  /** What its Routing status means to the rules (`routing-status.ts`): the window, shipping, the Routed tab. */
  routingCategory: StatusCategory;
  /** What came over from the lines its pick tickets were on (`carry.ts`). */
  carry: {
    sources: CarrySource[];
    /** Undecided fields: the stored ones and those this read found. */
    conflicts: CarryConflicts;
    /** Blank fields filled from the sources. */
    filled: WarehouseField[];
    /** "WMS 37218 (RL-12)": stored, or what this read found. */
    from: string;
    /** The line has a record. */
    saved: boolean;
  };
  finished: { status: FinishedStatus; at: string | null; by: string; note: string } | null;
  /** Every pick ticket is gone from A2000 (the "Gone from A2000" tab). */
  allGone: boolean;
  /** A No WMS row: one open pick ticket without a WMS #, read-only. */
  noWms: boolean;
  /** A History row from the old workbook (`xl-<source_row>`); null for the app's own lines. */
  excel: ExcelInfo | null;
  /** Stored values a rule would refuse, saved outside the grid (`integrity.ts`); [] for none. */
  outside: OutsideChange[];
  /** Its pick tickets the WMS shipped or billed (`a2000.ts`): a flag only; null for none. */
  a2000: A2000Shipped | null;
  /** On a pickup now (the Shipping schedule): its shipping row and the pickup; null when not. */
  onPickup: { row: ShippingRowRecord; pickup: PickupRecord } | null;
  /** How it left, when the app marked it shipped (the Ship log); null otherwise. */
  shipment: ShipmentRecord | null;
  /**
   * Its Appointment date (item 8): the date of the pickup it is on, or shipped on; null on none.
   * The stored `fields.appointmentDate` of a line on no pickup is "not scheduled" (`appointment.ts`).
   */
  pickupDate: string | null;
}

export const EMPTY_FIELDS: WarehouseValues = {
  readyDate: "",
  workNeeded: "",
  productionNotes: "",
  routingStatus: "Not routed",
  routingNotes: "",
  routingRef: "",
  schedShip: "",
  appointmentDate: "",
  inventoryNotes: "",
  lead: "",
  timing: "",
  notes: "",
  routedOn: "",
  peShipDate: "",
  pullGroup: "",
  locQty: "",
  peAssignedTo: "",
  peEstimate: "",
  peStatus: "",
  peStartAt: "",
  peEndAt: "",
  pallets: "",
  peComments: "",
};

const str = (v: unknown) => (v === null || v === undefined ? "" : String(v));
const num = (v: unknown) => (v === null || v === undefined || v === "" ? null : Number(v));

export function storedFields(r: RoutingLineRecord): WarehouseValues {
  const f = { ...EMPTY_FIELDS, ...peFields(r) };
  for (const k of WAREHOUSE_FIELDS) {
    if (isPeField(k)) continue;
    if (k === "routingStatus") f.routingStatus = r.routingStatus || "Not routed";
    else if (k === "routedOn" || k === "appointmentDate") f[k] = asDay(r[k]) ?? "";
    else f[k] = str(r[k]);
  }
  return f;
}

function ptFromSource(
  s: SourceRow,
  copy: LinePickTicketRecord | null,
  value: number | null,
  cameFrom: string | null = null,
): PtView {
  return {
    pickTicket: String(s.pick_ticket),
    source: s,
    copy,
    cameFrom,
    gone: false,
    orderNo: str(s.customer_po),
    customerPo: str(s.customer_po),
    customerCode: str(s.customer_code),
    customerName: str(s.customer_name),
    corp: str(s.corp),
    div: str(s.div),
    division: str(s.division),
    store: str(s.store),
    ptDate: asDay(s.pt_date),
    start: asDay(s.start_date),
    cancel: asDay(s.cancel_date),
    qty: Number(s.qty ?? 0),
    value,
    wms: str(s.wms_shipment),
    wh: str(s.wh),
    openBdays: num(s.routing_open_bdays),
    dueBdays: num(s.routing_due_bdays),
    a2000Rule: s.deposco ? NO_RULE : { openBdays: num(s.routing_open_bdays), dueBdays: num(s.routing_due_bdays) },
    dc: str(s.dc_name) || str(s.dc_no),
    deposco: s.deposco ?? null,
  };
}

/** A gone pick ticket from its copy; a Deposco SO with what Deposco says of it now that it is closed. */
function ptFromCopy(c: LinePickTicketRecord, closed?: DeposcoRow): PtView {
  return {
    pickTicket: c.pickTicket,
    source: null,
    copy: c,
    cameFrom: null,
    gone: true,
    orderNo: str(c.orderNo),
    customerPo: str(c.customerPo),
    customerCode: str(c.customerCode),
    customerName: str(c.customerName),
    corp: str(c.corp),
    div: str(c.div),
    division: str(c.division),
    store: str(c.store),
    ptDate: asDay(c.ptDate),
    start: asDay(c.startDate),
    cancel: asDay(c.cancelDate),
    qty: Number(c.qty ?? 0),
    value: null,
    wms: str(c.wmsShipment),
    wh: str(c.wh),
    openBdays: num(c.routingOpenBdays),
    dueBdays: num(c.routingDueBdays),
    // A Deposco SO's copy may hold the rule borrowed when it was saved: never its own.
    a2000Rule: isSoNo(String(c.pickTicket))
      ? NO_RULE
      : { openBdays: num(c.routingOpenBdays), dueBdays: num(c.routingDueBdays) },
    dc: "",
    deposco: closed ? deposcoInfo(closed) : null,
  };
}

const min = (xs: (string | null)[]) => xs.filter((x): x is string => !!x).sort()[0] ?? null;
const max = (xs: (string | null)[]) =>
  xs
    .filter((x): x is string => !!x)
    .sort()
    .at(-1) ?? null;
const uniq = (xs: string[]) => [...new Set(xs.filter(Boolean))];
/** Every value once, the first one's value first. */
const firstThen = (first: string, all: string[]) => uniq([first, ...[...all].sort()]);

/** `firstThen` for records: the first one, then the rest by `key`, each key once; blank keys left out. */
function firstThenBy<T>(first: T | undefined, all: readonly T[], key: (t: T) => string): T[] {
  const seen = new Set<string>();
  const out: T[] = [];
  for (const t of [...(first ? [first] : []), ...[...all].sort((a, b) => key(a).localeCompare(key(b)))]) {
    const k = key(t);
    if (!k || seen.has(k)) continue;
    seen.add(k);
    out.push(t);
  }
  return out;
}
const customerOf =
  (source: LineSource) =>
  (p: Pick<PtView, "customerCode" | "customerName">): CustomerRef => ({
    code: p.customerCode,
    name: p.customerName,
    source,
  });
/** The abbreviation is filled once every line is built (`assemble`'s `divisionAbbrevs`). */
const divisionOf = (p: Pick<PtView, "corp" | "div" | "division">): DivisionRef => ({
  corp: p.corp,
  div: p.div,
  name: p.division,
  abbrev: "",
});

/**
 * The pick ticket a line's names come from: the earliest ship window, by first ship then cancel
 * (a missing date last), then pick ticket — never whichever is listed first.
 */
export function leadPt<P extends Pick<PtView, "pickTicket" | "start" | "cancel">>(pts: readonly P[]): P | undefined {
  // ISO days compare as strings; a missing one last.
  const day = (x: string | null, y: string | null) => (x === y ? 0 : x === null ? 1 : y === null ? -1 : x < y ? -1 : 1);
  return [...pts].sort(
    (a, b) => day(a.start, b.start) || day(a.cancel, b.cancel) || a.pickTicket.localeCompare(b.pickTicket),
  )[0];
}

/** The copy's values in the source's terms, for old → new. */
function copyAggregate(copies: LinePickTicketRecord[]) {
  const days = copies.map((c) => asDay(c.ptDate));
  const pos = copies.map((c) => str(c.customerPo));
  return {
    start: min(copies.map((c) => asDay(c.startDate))),
    cancel: min(copies.map((c) => asDay(c.cancelDate))),
    // Compared only when every copy has it: a copy saved without one is no record of it.
    ptDate: days.every(Boolean) ? min(days) : undefined,
    po: pos.every(Boolean) ? poSet(pos) : undefined,
    qty: copies.reduce((n, c) => n + Number(c.qty ?? 0), 0),
  };
}
/** POs as one text, each once, sorted: "PO1, PO2". */
const poSet = (pos: readonly string[]) =>
  uniq(pos.flatMap((p) => p.split(",").map((x) => x.trim())))
    .filter(Boolean)
    .sort()
    .join(", ");

/**
 * Approved, or UPDATED with what changed since: in the source (A2000 or Deposco), any pick
 * ticket's First ship, Cancel or Received date, QTY, PO, or pick tickets (SOs) that came or went;
 * or a `TEAM_REVIEW_FIELDS` date against its value as approved (`team`; none on a move-over line,
 * which has no record of them). A comparison, never a flag: changed back, the line is Approved again.
 */
function sinceApproval(
  pts: PtView[],
  removed: PtView[],
  lostBaseline: boolean,
  team: { approved: Partial<RoutingLineRecord>; now: WarehouseValues } | null,
): { review: Review; changes: Changes; ptChanges: PtChange[] } {
  // An unreadable baseline is no record of what was approved: Updated, no old → new, and Approve writes it again.
  if (lostBaseline) return { review: "updated", changes: {}, ptChanges: [] };
  const live = pts.filter((p) => !p.gone);
  const held = live.filter((p) => p.copy);
  const changes: Changes = {};
  const ptChanges: PtChange[] = [];
  for (const p of held) {
    const c = p.copy!;
    const was = { start: asDay(c.startDate), cancel: asDay(c.cancelDate), ptDate: asDay(c.ptDate) };
    for (const field of ["start", "cancel", "ptDate"] as const)
      // A copy saved without its received date is no record of it.
      if (was[field] !== p[field] && (field !== "ptDate" || was.ptDate))
        ptChanges.push({ pickTicket: p.pickTicket, field, from: was[field], to: p[field] });
  }
  if (held.length) {
    const was = copyAggregate(held.map((p) => p.copy!));
    const now = {
      start: min(held.map((p) => p.start)),
      cancel: min(held.map((p) => p.cancel)),
      ptDate: min(held.map((p) => p.ptDate)),
      po: poSet(held.map((p) => p.customerPo)),
      qty: held.reduce((n, p) => n + p.qty, 0),
    };
    if (was.start !== now.start) changes.start = { from: was.start, to: now.start };
    if (was.cancel !== now.cancel) changes.cancel = { from: was.cancel, to: now.cancel };
    if (was.ptDate !== undefined && was.ptDate !== now.ptDate) changes.ptDate = { from: was.ptDate, to: now.ptDate };
    if (was.qty !== now.qty) changes.qty = { from: was.qty, to: now.qty };
    if (was.po !== undefined && was.po !== now.po) changes.po = { from: was.po || null, to: now.po || null };
  }
  const joined = live.some((p) => !p.copy);
  if (joined || removed.length) {
    changes.pts = {
      from: ptText({ pts: [...held, ...removed].map((p) => ({ ...p, gone: false })) }) || null,
      to: ptText({ pts: live }) || null,
    };
  }
  if (team)
    for (const [f, approvedField] of Object.entries(TEAM_REVIEW_FIELDS) as [TeamReviewField, string][]) {
      const was = str(team.approved[approvedField as keyof RoutingLineRecord]);
      const now = str(team.now[f]);
      if (was !== now) changes[f] = { from: was || null, to: now || null };
    }
  const updated = Object.keys(changes).length > 0 || ptChanges.length > 0;
  return { review: updated ? "updated" : "approved", changes: updated ? changes : {}, ptChanges };
}

interface BuildExtra {
  removed?: PtView[];
  sources?: CarrySource[];
  outside?: OutsideChange[];
  noWms?: boolean;
  /** A line from before WMS grouping, or a WMS line all of whose pick tickets are on other lines now. */
  regroupedAt?: string | null;
}

function buildLine(
  key: string,
  stored: RoutingLineRecord | null,
  pts: PtView[],
  manager: boolean,
  extra: BuildExtra = {},
): LineView {
  const { removed = [], sources = [], outside = [], noWms = false } = extra;
  const live = pts.filter((p) => !p.gone);
  const counted = live.length ? live : pts;
  const first = leadPt(counted);
  const values = live.map((p) => p.value);
  const own = stored ? storedFields(stored) : { ...EMPTY_FIELDS };
  const carried = sources.length ? carryOver(own, sources) : { fields: own, conflicts: {}, filled: [] };
  const conflicts = mergeConflicts(decodeConflicts(stored?.carryConflicts), carried.conflicts);
  const lostBaseline = outside.some((o) => o.field === "baseline1");

  let review: Review = "new";
  let changes: Changes = {};
  let ptChanges: PtChange[] = [];
  if (noWms) review = "nowms";
  else if (stored?.reviewStatus === "Split" || sources.some((s) => !s.legacy)) review = "split";
  else if (stored?.reviewStatus === "Approved")
    ({ review, changes, ptChanges } = sinceApproval(pts, removed, lostBaseline, {
      approved: stored,
      now: carried.fields,
    }));
  else if (
    !stored &&
    sources.length &&
    sources.every((s) => s.legacy && s.approved) &&
    !Object.keys(carried.conflicts).length
  )
    // The move-over: approved where every line it came from was, with no conflict.
    ({ review, changes, ptChanges } = sinceApproval(pts, removed, false, null));

  const finished: LineView["finished"] = stored?.finishedStatus
    ? {
        status: stored.finishedStatus,
        at: asDay(stored.finishedAt),
        by: str(stored.finishedBy),
        note: str(stored.finishedNote),
      }
    : extra.regroupedAt !== undefined
      ? { status: "Regrouped by WMS", at: extra.regroupedAt, by: "", note: "" }
      : null;

  const customers = live.map((p) => p.customerName);
  const divisions = live.map((p) => p.division);
  const whs = live.map((p) => p.wh);
  const starts = live.map((p) => p.start ?? "");
  const cancels = live.map((p) => p.cancel ?? "");
  const mixed: Mixed = {
    customers: uniq(customers).length,
    divisions: uniq(divisions).length,
    warehouses: uniq(whs).length,
    starts: uniq(starts).length,
    cancels: uniq(cancels).length,
  };

  const groupKey = stored?.groupKey ?? (isVirtualKey(key) ? key.slice(VIRTUAL_PREFIX.length) : "");
  const source = sourceOfKey(groupKey);
  const deposcos = counted.map((p) => p.deposco).filter((d): d is DeposcoInfo => !!d);
  const deposco: DeposcoLine | null =
    source === "Deposco"
      ? {
          status: deposcoStatus(counted.map((p) => ({ so: p.pickTicket, status: p.deposco?.status ?? "" }))),
          shipped: deposcoShipped(pts.flatMap((p) => (p.deposco ? [{ ...p.deposco, so: p.pickTicket }] : []))),
          dlixes: uniq(deposcos.map((d) => d.dlix)),
          cos: uniq(deposcos.map((d) => d.co)),
          customerOrders: uniq(deposcos.map((d) => d.customerOrder)),
          groups: uniq(deposcos.map((d) => d.group)),
        }
      : null;

  return {
    key,
    stored,
    groupKey,
    source,
    deposco,
    wmsNo: stored ? wmsOf(stored) : groupKey.startsWith(WMS_PREFIX) ? groupKey.slice(WMS_PREFIX.length) : "",
    pts: [...pts].sort((a, b) => Number(a.gone) - Number(b.gone) || a.pickTicket.localeCompare(b.pickTicket)),
    removed,
    corp: str(first?.corp),
    customerCode: str(first?.customerCode),
    customerName: str(first?.customerName),
    customerNames: firstThen(
      str(first?.customerName),
      counted.map((p) => p.customerName),
    ),
    customers: firstThenBy(
      first && customerOf(source)(first),
      counted.map(customerOf(source)),
      (c) => c.code || c.name,
    ),
    div: str(first?.div),
    division: str(first?.division),
    divisions: firstThen(
      str(first?.division),
      counted.map((p) => p.division),
    ),
    divs: firstThenBy(first && divisionOf(first), counted.map(divisionOf), (d) =>
      d.name ? `${d.name}|${d.corp}|${d.div}` : "",
    ),
    wh: str(first?.wh),
    whs: firstThen(
      str(first?.wh),
      counted.map((p) => p.wh),
    ),
    ptDate: max(counted.map((p) => p.ptDate)),
    ptDates: uniq(counted.map((p) => p.ptDate ?? "")).sort(),
    start: min(counted.map((p) => p.start)),
    starts: uniq(counted.map((p) => p.start ?? "")).sort(),
    cancel: min(counted.map((p) => p.cancel)),
    cancels: uniq(counted.map((p) => p.cancel ?? "")).sort(),
    qty: counted.reduce((n, p) => n + p.qty, 0),
    value:
      manager && values.length && values.every((v) => v !== null) ? values.reduce((n, v) => n + (v ?? 0), 0) : null,
    wms: uniq(counted.map((p) => p.wms)).join(", "),
    dcs: uniq(counted.map((p) => p.dc)),
    orderNos: uniq(counted.map((p) => p.orderNo)),
    pos: uniq(counted.flatMap((p) => p.customerPo.split(",").map((x) => x.trim()))),
    mixed: Object.values(mixed).some((n) => n > 1) ? mixed : null,
    review,
    changes,
    ptChanges,
    fields: carried.fields,
    // The list's own category is set once every line is built (`assemble`); the starting seven's till then.
    routingCategory: statusCategory(carried.fields.routingStatus),
    carry: {
      sources,
      conflicts,
      filled: carried.filled,
      from: carriedFromText(sources, str(stored?.carriedFrom)),
      saved: !!stored,
    },
    finished,
    allGone: !noWms && extra.regroupedAt === undefined && pts.length > 0 && live.length === 0,
    noWms,
    excel: null,
    outside,
    a2000: null,
    onPickup: null,
    shipment: null,
    pickupDate: null,
  };
}

export interface Assembled {
  lines: LineView[];
}

/** Every line, open and finished, and every No WMS row. The caller filters (`filters.ts`). */
export function assemble(input: {
  source: readonly SourceRow[];
  lines: readonly RoutingLineRecord[];
  pickTickets: readonly LinePickTicketRecord[];
  manager: boolean;
  /** Pick ticket → $ value; managers only. */
  values?: Readonly<Record<string, number>> | null;
  /** The lists, so a stored list value outside them is flagged; absent, lists are not checked. */
  listValues?: readonly ListValueRecord[];
  /** Today, so a stored date over a year ahead is flagged; absent, only the format is. */
  today?: string;
  /** Pickups, shipping rows and shipments: each line's pickup and shipment; absent, none. */
  pickups?: StoredShipping["pickups"];
  shippingRows?: StoredShipping["shippingRows"];
  shipments?: StoredShipping["shipments"];
  /** The A2000 shipments of the lines' pick tickets (SHIPPED or BILLED): the flag; absent, none. */
  ptShipments?: readonly PtShipmentRow[] | null;
  /** The saved division abbreviations; absent, every division shows its default. */
  divisionAbbrevs?: readonly DivisionAbbrevRecord[];
  customerNames?: readonly CustomerNameRecord[];
  /** The saved routing rules (Admin → Routing rules); absent, A2000's only. */
  routingRules?: readonly RoutingRuleRecord[];
  /** A2000's rules beyond the rows at hand (a subset's); absent, `input.source`'s. */
  ruleBase?: RuleBase | null;
  /** The Deposco SOs the lines hold that are closed now: how each gone SO left; absent, none known. */
  deposcoClosed?: readonly DeposcoRow[] | null;
}): Assembled {
  const storedById = new Map(input.lines.map((l) => [l.id, l]));
  // What a rule would refuse on each stored line (`integrity.ts`): shown, never thrown.
  const outside = new Map<string, OutsideChange[]>();
  const flag = (lineId: string, change: OutsideChange) => outside.set(lineId, [...(outside.get(lineId) ?? []), change]);
  for (const l of input.lines) for (const c of fieldChanges(l, input.listValues, input.today)) flag(l.id, c);

  // Each line's copies: its baseline, except a pick ticket that has a record (placed by hand
  // before WMS grouping, or a baseline too big), which wins wherever it is.
  const records = new Map<string, LinePickTicketRecord>();
  for (const c of input.pickTickets) {
    const why = copyProblem(c);
    if (!why) records.set(c.pickTicket, c);
    else if (storedById.has(c.lineId))
      flag(c.lineId, { field: "pickTicket", label: `Pick ticket ${String(c.pickTicket).slice(0, 20)}`, why });
  }
  const copiesOf = new Map<string, Map<string, LinePickTicketRecord>>();
  const hold = (c: LinePickTicketRecord) => {
    if (!storedById.has(c.lineId)) return;
    const m = copiesOf.get(c.lineId) ?? new Map<string, LinePickTicketRecord>();
    m.set(c.pickTicket, c);
    copiesOf.set(c.lineId, m);
  };
  for (const l of input.lines) {
    const { copies, problem } = readBaseline(l);
    if (problem) flag(l.id, problem);
    for (const c of copies) if (!records.has(c.pickTicket)) hold(c);
  }
  for (const c of records.values()) hold(c);
  /** The lines holding a copy of each pick ticket. */
  const holders = new Map<string, string[]>();
  for (const [lineId, m] of copiesOf) for (const pt of m.keys()) holders.set(pt, [...(holders.get(pt) ?? []), lineId]);

  const valueOf = (s: SourceRow) => input.values?.[String(s.pick_ticket)] ?? null;
  const open = new Map(input.source.map((s) => [String(s.pick_ticket), s]));
  const closed = new Map((input.deposcoClosed ?? []).map((r) => [String(r.so_number), r]));
  const fromCopy = (c: LinePickTicketRecord) => ptFromCopy(c, closed.get(c.pickTicket));

  // The open line of each group — WMS # or Deposco line (two would be a race of first saves: the older one).
  const lineOfGroup = new Map<string, RoutingLineRecord>();
  for (const l of [...input.lines].sort((a, b) => a.id.localeCompare(b.id, undefined, { numeric: true }))) {
    const g = groupOf(l);
    if (isGroupedLine(l) && g && !l.finishedStatus && !lineOfGroup.has(g)) lineOfGroup.set(g, l);
  }

  /**
   * The line a pick ticket came from: another open line holding its copy — a grouped line before a
   * line from before WMS grouping, then the one saved last. A finished line is never a source.
   */
  const cameFrom = (pt: string, here: string): RoutingLineRecord | null => {
    const others = (holders.get(pt) ?? [])
      .filter((id) => id !== here)
      .map((id) => storedById.get(id)!)
      .filter((l) => !l.finishedStatus);
    others.sort(
      (a, b) =>
        Number(isGroupedLine(b)) - Number(isGroupedLine(a)) || str(b.updated_at).localeCompare(str(a.updated_at)),
    );
    return others[0] ?? null;
  };
  const sourceOf = (l: RoutingLineRecord): CarrySource => ({
    lineId: l.id,
    wms: wmsOf(l),
    legacy: !isGroupedLine(l),
    approved: l.reviewStatus === "Approved",
    fields: storedFields(l),
  });

  // The open pick tickets by line: one already on a line of its group stays there (finished or
  // not), as does one on a finished line from before WMS grouping — a finished line keeps its pick
  // tickets. Any other joins the open line of its group, or a virtual one. Those without a WMS #
  // are rows of their own.
  const groups = new Map<string, { group: string; rows: SourceRow[] }>();
  const noWms: SourceRow[] = [];
  for (const s of input.source) {
    const group = groupOfSource(s);
    if (!group) {
      noWms.push(s);
      continue;
    }
    const home = (holders.get(String(s.pick_ticket)) ?? [])
      .map((id) => storedById.get(id)!)
      .filter((l) => (isGroupedLine(l) && groupOf(l) === group) || (!isGroupedLine(l) && l.finishedStatus))
      .sort((a, b) => Number(!!a.finishedStatus) - Number(!!b.finishedStatus))[0];
    const key = home?.id ?? lineOfGroup.get(group)?.id ?? VIRTUAL_PREFIX + group;
    const g = groups.get(key) ?? { group, rows: [] };
    g.rows.push(s);
    groups.set(key, g);
  }

  const lines: LineView[] = [];
  const used = new Set<string>();
  for (const [key, { group, rows }] of groups) {
    const stored = storedById.get(key) ?? null;
    if (stored) used.add(stored.id);
    const own = (stored && copiesOf.get(stored.id)) || new Map<string, LinePickTicketRecord>();
    // The lines it has already carried from: never again (`carry.ts`).
    const carried = carriedIds(stored?.carriedFrom);
    const pts: PtView[] = [];
    const sources = new Map<string, CarrySource>();
    for (const s of rows) {
      const pt = String(s.pick_ticket);
      const mine = own.get(pt);
      if (mine) {
        pts.push(ptFromSource(s, mine, valueOf(s)));
        continue;
      }
      const from = cameFrom(pt, key);
      pts.push(ptFromSource(s, from ? copiesOf.get(from.id)!.get(pt)! : null, valueOf(s), from?.id ?? null));
      if (from && !carried.has(from.id) && !sources.has(from.id)) sources.set(from.id, sourceOf(from));
    }
    // Each source as the team knows it: the pick tickets that came from it.
    for (const src of sources.values()) {
      const from = storedById.get(src.lineId)!;
      src.about = sourceAbout(
        src.wms ? `WMS ${src.wms}` : isDeposcoKey(from.groupKey) ? deposcoPlace(from.groupKey) : "",
        pts.filter((p) => p.cameFrom === src.lineId),
        ptWord({ source: sourceOfKey(from.groupKey) }),
      );
    }
    const removed: PtView[] = [];
    for (const [pt, c] of own) {
      const s = open.get(pt);
      if (!s) pts.push(fromCopy(c));
      else if (groupOfSource(s) !== group)
        removed.push({ ...ptFromSource(s, c, valueOf(s)), wms: s.in_wms === 1 ? str(s.wms_shipment) : "" });
    }
    lines.push(
      buildLine(key, stored, pts, input.manager, {
        removed,
        sources: [...sources.values()],
        outside: stored ? outside.get(stored.id) : undefined,
      }),
    );
  }

  // Stored lines with none of their pick tickets open on them: finished, gone from A2000, or — a
  // line from before WMS grouping, or a WMS line A2000 moved every pick ticket of — regrouped.
  for (const l of input.lines) {
    if (used.has(l.id)) continue;
    const copies = [...(copiesOf.get(l.id)?.values() ?? [])];
    const changes = outside.get(l.id);
    if (!copies.length && !changes) continue;
    const elsewhere = copies.filter((c) => open.has(c.pickTicket));
    const regrouped = !l.finishedStatus && (!isGroupedLine(l) || elsewhere.length > 0);
    const pts = copies.map((c) => {
      const s = open.get(c.pickTicket);
      return s && !regrouped ? ptFromSource(s, c, valueOf(s)) : fromCopy(c);
    });
    lines.push(
      buildLine(l.id, l, pts, input.manager, {
        outside: changes,
        ...(regrouped ? { regroupedAt: asDay(l.updated_at) ?? input.today ?? null } : {}),
      }),
    );
  }

  // No WMS # yet: one read-only row per pick ticket, until A2000 gives it one.
  for (const s of noWms) {
    const pt = String(s.pick_ticket);
    lines.push(
      buildLine(NO_WMS_PREFIX + pt, null, [ptFromSource(s, null, valueOf(s))], input.manager, { noWms: true }),
    );
  }
  withShipping(lines, input, input.ptShipments);
  withAbbrevs(lines, divisionNamer(input.divisionAbbrevs));
  withCustomerNames(lines, customerNamer(input.customerNames));
  withRoutingRules(
    lines,
    ruleContext({
      routingRules: input.routingRules,
      customerNames: input.customerNames,
      base: input.ruleBase ?? ruleBase(input.source),
    }),
  );
  for (const l of lines) l.routingCategory = statusCategory(l.fields.routingStatus, input.listValues);
  return { lines };
}

/** Each pick ticket's routing rule in use (`routingRuleFor`): the window's numbers, and where they came from. */
export function withRoutingRules(lines: readonly Pick<LineView, "pts" | "removed" | "source">[], ctx: RuleContext) {
  for (const l of lines)
    for (const p of [...l.pts, ...l.removed]) {
      const { openBdays, dueBdays, ...rule } = routingRuleFor(
        { source: l.source, code: p.customerCode, name: p.customerName, a2000: p.a2000Rule },
        ctx,
      );
      Object.assign(p, { openBdays, dueBdays, rule });
    }
}

/** Each line's divisions with the abbreviation the pages show: saved, else the default. */
export function withAbbrevs(lines: readonly Pick<LineView, "divs">[], name: (d: DivisionRef) => string) {
  for (const l of lines) for (const d of l.divs) d.abbrev = name(d);
}

/**
 * Each line's A2000 flag, its pickup (a row On pickup of a Scheduled pickup, the last placed) and
 * its shipment (the last made). By stored id: a line nobody saved is on no pickup.
 */
function withShipping(
  lines: LineView[],
  shipping: Partial<StoredShipping>,
  ptShipments: readonly PtShipmentRow[] | null | undefined,
) {
  const byPt = new Map((ptShipments ?? []).map((r) => [String(r.pick_ticket), r]));
  const pickups = new Map((shipping.pickups ?? []).map((p) => [p.id, p]));
  const newer = (a: { created_at?: string } | undefined, b: { created_at?: string }) =>
    !a || str(b.created_at).localeCompare(str(a.created_at)) >= 0;
  const rowOf = new Map<string, { row: ShippingRowRecord; pickup: PickupRecord }>();
  for (const row of shipping.shippingRows ?? []) {
    const pickup = pickups.get(row.pickupId);
    if (row.status !== "On pickup" || pickup?.status !== "Scheduled") continue;
    if (newer(rowOf.get(row.lineId)?.row, row)) rowOf.set(row.lineId, { row, pickup });
  }
  const shipmentOf = new Map<string, ShipmentRecord>();
  for (const s of shipping.shipments ?? []) if (newer(shipmentOf.get(s.lineId), s)) shipmentOf.set(s.lineId, s);
  for (const l of lines) {
    if (!l.noWms && l.source === "A2000" && byPt.size) l.a2000 = a2000Shipped(l.pts, byPt);
    const id = l.stored?.id;
    if (!id) continue;
    l.onPickup = l.finished ? null : (rowOf.get(id) ?? null);
    l.shipment = shipmentOf.get(id) ?? null;
    l.pickupDate =
      l.onPickup?.pickup.pickupDate ??
      (l.shipment?.pickupId ? (pickups.get(l.shipment.pickupId)?.pickupDate ?? null) : null);
  }
}

/** The stored copy of a pick ticket's source fields — what Approve writes. */
export function copyInput(s: SourceRow, lineId: string): Omit<LinePickTicketRecord, "id"> {
  return {
    lineId,
    pickTicket: String(s.pick_ticket),
    wh: str(s.wh),
    orderNo: str(s.customer_po),
    corp: str(s.corp),
    customerCode: str(s.customer_code),
    customerName: str(s.customer_name),
    div: str(s.div),
    division: str(s.division),
    store: str(s.store),
    customerPo: str(s.customer_po).slice(0, 400),
    ptDate: asDay(s.pt_date),
    startDate: asDay(s.start_date),
    cancelDate: asDay(s.cancel_date),
    qty: s.qty === null ? null : Number(s.qty),
    wmsShipment: str(s.wms_shipment),
    routingOpenBdays: s.routing_open_bdays,
    routingDueBdays: s.routing_due_bdays,
    sourceLoadedAt: str(s.source_loaded_at).slice(0, 30),
  };
}

/** A copy record as an update input (every field, as the CRUD API wants). */
export function copyRecordInput(c: LinePickTicketRecord, lineId = c.lineId): Omit<LinePickTicketRecord, "id"> {
  const { id: _id, ...rest } = c;
  return {
    ...rest,
    lineId,
    ptDate: asDay(c.ptDate),
    startDate: asDay(c.startDate),
    cancelDate: asDay(c.cancelDate),
    qty: num(c.qty),
    routingOpenBdays: num(c.routingOpenBdays),
    routingDueBdays: num(c.routingDueBdays),
    wh: str(c.wh),
    orderNo: str(c.orderNo),
    corp: str(c.corp),
    customerCode: str(c.customerCode),
    customerName: str(c.customerName),
    div: str(c.div),
    division: str(c.division),
    store: str(c.store),
    customerPo: str(c.customerPo),
    wmsShipment: str(c.wmsShipment),
    sourceLoadedAt: str(c.sourceLoadedAt),
  };
}

/** A line as a full `routing-line` input: its stored record, or a new one for a virtual line. */
export function lineInput(line: LineView, changes: Partial<Omit<RoutingLineRecord, "id">> = {}) {
  const s = line.stored;
  const base: Omit<RoutingLineRecord, "id" | "updated_at"> = {
    groupKey: line.groupKey.slice(0, 160),
    grouping: s?.grouping ?? "Automatic",
    groupedBy: str(s?.groupedBy),
    groupedAt: str(s?.groupedAt),
    ...(storedLineFields(line.fields) as Omit<WarehouseValues, PeField> & Partial<PeStored>),
    // A line nobody saved yet keeps what it shows: Split, or Approved from the move-over.
    reviewStatus:
      s?.reviewStatus ??
      (line.review === "split"
        ? "Split"
        : line.review === "approved" || line.review === "updated"
          ? "Approved"
          : "Not reviewed"),
    reviewedBy: str(s?.reviewedBy),
    reviewedAt: str(s?.reviewedAt),
    finishedStatus: s?.finishedStatus ?? null,
    finishedAt: asDay(s?.finishedAt),
    finishedBy: str(s?.finishedBy),
    finishedNote: str(s?.finishedNote),
    // Never carried to a new record: a line's baseline is its own pick tickets.
    ...NO_BASELINE,
    // As approved: stored, else what a line the move-over shows Approved shows now (its first save).
    ...(Object.fromEntries(
      Object.entries(TEAM_REVIEW_FIELDS).map(([f, approved]) => [
        approved,
        s
          ? (s[approved] ?? null)
          : line.review === "approved" || line.review === "updated"
            ? line.fields[f as TeamReviewField] || null
            : null,
      ]),
    ) as Pick<RoutingLineRecord, "approvedReadyDate" | "approvedSchedShip">),
    ...carryInput(line),
  };
  const merged = { ...base, ...changes };
  return {
    ...merged,
    routedOn: merged.routedOn || null,
    appointmentDate: merged.appointmentDate || null,
    finishedAt: merged.finishedAt || null,
  };
}

/**
 * What a save of a line keeps of its carry-over: its WMS #, the entries it brought (filled blanks),
 * what is still undecided and where it came from. Written with any save while the line shows them,
 * so the next read — which no longer sees the pick tickets as coming from elsewhere — keeps them.
 */
export function carryInput(
  line: LineView,
): Pick<RoutingLineRecord, "wmsNo" | "carryConflicts" | "carriedFrom"> &
  Partial<Pick<RoutingLineRecord, "reviewStatus">> &
  Partial<Omit<WarehouseValues, PeField>> &
  Partial<PeStored> {
  const filled = Object.fromEntries(line.carry.filled.map((f) => [f, line.fields[f]])) as Partial<WarehouseValues>;
  return {
    ...storedLineFields(filled),
    // A Split line stays Split until approved (Approve's own status comes after this).
    ...(line.review === "split" ? { reviewStatus: "Split" as const } : {}),
    wmsNo: line.wmsNo.slice(0, 40),
    carryConflicts: encodeConflicts(line.carry.conflicts),
    carriedFrom: line.carry.from.slice(0, 500),
  };
}

/** The fields an edit also changes (spec §5 and the mockup): Routed on, and finishing on Cancelled. */
export function editSideEffects(
  line: LineView,
  field: WarehouseField,
  value: string,
  who: string,
  today: string,
  /** The new status's category, from the Routing status list (`statusCategory`). */
  category: StatusCategory = statusCategory(value),
  /** Now, to the minute, local ("2026-10-30 14:05"): PE Order Status sets Start and End Time. */
  now = "",
): Partial<Omit<RoutingLineRecord, "id">> {
  const out: Partial<Omit<RoutingLineRecord, "id">> = {};
  if (field === "peStatus" && now) return peStatusTimes(value, line.fields, now);
  if (field !== "routingStatus") return out;
  if ((category === "Routed" || category === "Requested") && !line.fields.routedOn) out.routedOn = today;
  if (category === "Cancelled") {
    out.finishedStatus = "Cancelled";
    out.finishedAt = today;
    out.finishedBy = who;
  } else if (line.finished?.status === "Cancelled" && line.routingCategory === "Cancelled") {
    // Undo of a cancel: the line is open again.
    out.finishedStatus = null;
    out.finishedAt = null;
    out.finishedBy = "";
    out.finishedNote = "";
  }
  return out;
}

/**
 * What puts a save of `field` back as one step (Ctrl+Z): its old value, and the old value of every
 * warehouse field its side effects (`editSideEffects`) changed — a Routed status's Routed on.
 * Finishing and reopening are `editSideEffects`'s own, both ways.
 */
export function undoOfEdit(
  line: Pick<LineView, "fields">,
  field: WarehouseField,
  side: Partial<Omit<RoutingLineRecord, "id">>,
): { field: WarehouseField; value: string; also: Partial<Record<WarehouseField, string>> } {
  const also: Partial<Record<WarehouseField, string>> = {};
  for (const k of Object.keys(side))
    if ((WAREHOUSE_FIELDS as readonly string[]).includes(k) && k !== field)
      also[k as WarehouseField] = line.fields[k as WarehouseField];
  return { field, value: line.fields[field], also };
}

export type { WarehouseField };
